import { apiFetch, apiJson, ApiError } from '@/lib/api';

type GetToken = () => Promise<string | null>;

export type ClioStatus = {
  configured: boolean;
  connected: boolean;
  clio_user_name?: string | null;
  clio_user_id?: string | null;
  connected_at?: string | null;
};

export type ClioMatter = {
  id: number;
  display_number?: string | null;
  description?: string | null;
  status?: string | null;
  client_id?: number | null;
  client_name?: string | null;
};

export type ClioMatterLink = {
  invite_id: number;
  clio_matter_id: number;
  clio_matter_display_number?: string | null;
  clio_matter_description?: string | null;
  clio_contact_id?: number | null;
  clio_contact_name?: string | null;
};

export type SendToClioResult = {
  document_id: number;
  filename: string;
  matter_id: number;
  section: string;
};

export function fetchClioStatus(getToken: GetToken): Promise<ClioStatus> {
  return apiJson<ClioStatus>(getToken, '/v1/professional/clio/status');
}

export async function startClioAuthorize(getToken: GetToken): Promise<string> {
  const res = await apiJson<{ authorize_url: string }>(
    getToken,
    '/v1/professional/clio/authorize'
  );
  return res.authorize_url;
}

export async function disconnectClio(getToken: GetToken): Promise<void> {
  const res = await apiFetch(getToken, '/v1/professional/clio/connection', {
    auth: true,
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const text = await res.text().catch(() => '');
    throw new ApiError(`Disconnect failed: ${res.status}`, res.status, text);
  }
}

export function fetchClioMatters(
  getToken: GetToken,
  query?: string
): Promise<{ matters: ClioMatter[] }> {
  const qs = query?.trim()
    ? `?${new URLSearchParams({ query: query.trim() }).toString()}`
    : '';
  return apiJson(getToken, `/v1/professional/clio/matters${qs}`);
}

export function fetchClioMatterLink(
  getToken: GetToken,
  inviteId: number
): Promise<ClioMatterLink | null> {
  return apiJson(getToken, `/v1/professional/clients/${inviteId}/clio-matter`);
}

export function setClioMatterLink(
  getToken: GetToken,
  inviteId: number,
  matter: ClioMatter
): Promise<ClioMatterLink> {
  return apiJson(getToken, `/v1/professional/clients/${inviteId}/clio-matter`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      clio_matter_id: matter.id,
      clio_matter_display_number: matter.display_number ?? null,
      clio_matter_description: matter.description ?? null,
      clio_contact_id: matter.client_id ?? null,
      clio_contact_name: matter.client_name ?? null,
    }),
  });
}

export function sendCounselReportToClio(
  getToken: GetToken,
  inviteId: number,
  section: string,
  matterId?: number
): Promise<SendToClioResult> {
  return apiJson(getToken, `/v1/professional/clients/${inviteId}/export/clio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      section,
      ...(matterId != null ? { matter_id: matterId } : {}),
    }),
  });
}

export function formatClioMatterLabel(matter: {
  display_number?: string | null;
  description?: string | null;
  client_name?: string | null;
  id: number;
}): string {
  const num = matter.display_number?.trim();
  const desc = matter.description?.trim();
  const client = matter.client_name?.trim();
  const parts = [num || `#${matter.id}`, desc, client].filter(Boolean);
  return parts.join(' · ');
}
